import type { SummonKind } from "../../../data/portal-ffa-catalog";
import { portalFfaCatalog } from "../../../data/portal-ffa-catalog";
import hudPresetTable from "../../../data/tables/ui/huds/portal-ffa.json";
import type {
  SummonRequestResult,
  WorldState,
} from "../../../features/portal-ffa";
import {
  getHudSnapshot,
  reenterTeam,
  requestSummon,
} from "../../../features/portal-ffa";
import { PinchTracker } from "../../../input/pinch-tracker";
import { getUiLayout } from "../../layout";
import type { HudShellHandle } from "../../layout/hud-shell";
import type { UiHudPreset } from "../../layout/types";
import {
  ICON_MANA,
  ICON_PRESERVE,
  ICON_QUEUE,
  ICON_SIGIL,
  deathModalHtml,
  midRegionHtml,
  topRegionHtml,
} from "./markup";
import { paintMinimap } from "./minimap";
import { mountSummonDock } from "./summon-dock";
import type { PortalFfaHudHandlers } from "./types";

export type { CameraViewRect, PortalFfaHudHandlers } from "./types";

const preset = hudPresetTable as UiHudPreset;

/**
 * FFA HUD glue — binds snapshot/handlers into layout shell regions.
 */
export function mountPortalFfaHud(
  handlers: PortalFfaHudHandlers,
): {
  sync: (world: WorldState) => void;
  feedbackSummon: (kind: SummonKind, result: SummonRequestResult) => void;
  destroy: () => void;
} {
  const ui = getUiLayout();
  ui.sync();
  const shell: HudShellHandle = ui.mountShell(preset);

  const top = shell.region("top");
  top.style.pointerEvents = "none";
  top.innerHTML = topRegionHtml();

  const minimapWrap = top.querySelector(
    "[data-minimap-wrap]",
  ) as HTMLElement | null;
  if (minimapWrap) minimapWrap.style.pointerEvents = "auto";

  const canvas = top.querySelector("[data-minimap]") as HTMLCanvasElement | null;
  if (canvas) {
    canvas.style.pointerEvents = "none";
    canvas.style.cursor = "default";
  }

  const zoomIn = top.querySelector("[data-zoom-in]");
  const zoomOut = top.querySelector("[data-zoom-out]");
  const step = portalFfaCatalog.camera.zoomStep;
  zoomIn?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onZoom(step);
  });
  zoomOut?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onZoom(-step);
  });

  const mid = shell.region("mid");
  mid.style.pointerEvents = "none";
  mid.innerHTML = midRegionHtml();
  const fieldHit = mid.querySelector("[data-field-hit]") as HTMLElement | null;
  let pinchTracker: PinchTracker | null = null;
  if (fieldHit) {
    fieldHit.style.pointerEvents = "auto";
    pinchTracker = new PinchTracker({
      onSingleDown: (x, y) => handlers.onFieldDown(x, y),
      onSingleMove: (x, y) => handlers.onFieldMove(x, y),
      onSingleUp: (x, y) => handlers.onFieldUp(x, y),
      onPinchStart: () => handlers.onPinchStart(),
      onPinchScale: (scale) => handlers.onPinchScale(scale),
      onPinchEnd: () => handlers.onPinchEnd(),
    });
    pinchTracker.attach(fieldHit);
    fieldHit.addEventListener(
      "wheel",
      (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const zoomStep = portalFfaCatalog.camera.zoomStep;
        handlers.onZoom(ev.deltaY > 0 ? -zoomStep : zoomStep);
      },
      { passive: false },
    );
  }

  const bottom = shell.region("bottom");
  bottom.style.pointerEvents = "auto";
  bottom.style.display = "flex";
  bottom.style.flexDirection = "column";
  bottom.style.gap = "6px";
  const toast = document.createElement("div");
  toast.className = "ffa-dock-toast";
  toast.dataset.toast = "1";
  toast.hidden = true;
  bottom.appendChild(toast);
  let toastTimer = 0;

  const showToast = (text: string): void => {
    toast.hidden = false;
    toast.textContent = text;
    toast.classList.add("is-show");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove("is-show");
      toast.hidden = true;
    }, 1600);
  };

  const kinds = (preset.dock?.kinds ?? []) as SummonKind[];
  const dock = mountSummonDock(
    bottom,
    kinds,
    handlers.onSummon,
    showToast,
  );

  const modal = shell.region("modal");
  modal.hidden = true;
  modal.innerHTML = deathModalHtml();
  modal.style.pointerEvents = "auto";
  modal.querySelector("[data-reenter]")?.addEventListener("click", () => {
    handlers.onReenter();
  });
  modal.querySelector("[data-quit]")?.addEventListener("click", () => {
    handlers.onQuit();
  });

  const sync = (world: WorldState): void => {
    const snap = getHudSnapshot(world);
    const ranks = top.querySelector("[data-ranks]");
    if (ranks) {
      ranks.innerHTML = snap.ranks
        .map(
          (r) =>
            `<div class="${r.isPlayer ? "me" : ""}">#${r.rank} ${r.name} · ${r.value}</div>`,
        )
        .join("");
    }
    const mana = top.querySelector("[data-mana]");
    if (mana) {
      mana.innerHTML =
        `<span class="ffa-stat">${ICON_MANA}<b>${snap.mana}</b></span>` +
        `<span class="ffa-stat">${ICON_PRESERVE}<b>${snap.preserved}</b></span>`;
    }
    const sigil = top.querySelector("[data-sigil]");
    if (sigil) {
      sigil.innerHTML =
        `<span class="ffa-stat">${ICON_SIGIL}<b>${snap.sigils}</b><small>+${snap.nextSigilNeed}</small></span>` +
        `<span class="ffa-stat">${ICON_QUEUE}<b>${snap.queueLen}</b></span>`;
    }
    const msg = top.querySelector("[data-msg]");
    if (msg) msg.textContent = snap.message;

    dock.syncAfford(world, snap);
    paintMinimap(canvas, world, handlers.getViewRect, snap);

    if (snap.pendingReenter && snap.runSummary) {
      modal.hidden = false;
      const pre = modal.querySelector("[data-summary]");
      if (pre) {
        const s = snap.runSummary;
        pre.textContent =
          `생존 ${s.surviveSec.toFixed(1)}s\n` +
          `최고 랭킹 #${s.peakRank}\n` +
          `마스터 처치 ${s.mastersKilled}\n` +
          `최대 병력 ${s.peakArmy}\n` +
          `누적 마나 ${s.lifetimeMana}\n` +
          `드랍 ${s.lastDrop}`;
      }
    } else {
      modal.hidden = true;
    }
  };

  return {
    sync,
    feedbackSummon: dock.feedbackSummon,
    destroy: () => {
      pinchTracker?.detach();
      shell.destroy();
    },
  };
}

export function wireSummon(
  world: WorldState,
  kind: SummonKind,
): SummonRequestResult {
  return requestSummon(world, 0, kind, { allowQueue: true });
}

export function wireReenter(world: WorldState): void {
  reenterTeam(world, 0);
}
